import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

// Existing project modules — unchanged
import { auth } from "../authV2";
import {IconSidebarV2} from "./../IconSidebar_ChatHistory/IconSidebarV2";

import "./PublicPageShared.css";

// Design system primitives (Phase 2)
import {
  Button,
  Input,
  Spinner,
  ThemeToggle,
  toast,
  cn,
  SunIcon, // not used here directly, kept for reference
} from "../../../styles/facemeet-ui";

import {
  Brand,
  ThemeSlot,
  Ambient,
} from "./PublicHelpers";

export function LoginPageV2() {
  const navigate = useNavigate();

  const [fdaId, setFdaId] = useState("");
  const [password, setPassword] = useState("");
  const [showPwd, setShowPwd] = useState(false);
  const [signingIn, setSigningIn] = useState(false);
  const [signInError, setSignInError] = useState("");

  const [roomId, setRoomId] = useState("");
  const [joinError, setJoinError] = useState("");

  // If already signed in, bounce to /v2
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const sess = await auth.validate();
      if (sess && !cancelled) navigate("/v2", { replace: true });
    })();
    return () => { cancelled = true; };
  }, [navigate]);

  const handleSignIn = async (e) => {
    e.preventDefault();
    setSignInError("");
    const id = fdaId.trim();
    if (!id) return setSignInError("Please enter your ID.");
    if (!password) return setSignInError("Please enter your password.");
    try {
      setSigningIn(true);
      await auth.login(id, password);
      toast.success("Welcome back");
      navigate("/v2", { replace: true });
    } catch (err) {
      setSignInError(err.message || "Sign-in failed.");
    } finally {
      setSigningIn(false);
    }
  };

  const handleQuickJoin = (e) => {
    e.preventDefault();
    setJoinError("");
    const raw = roomId.trim();
    if (!raw) return setJoinError("Enter a Room ID or paste a meeting link.");
    let id = raw;
    try {
      if (raw.startsWith("http")) {
        const u = new URL(raw);
        const parts = u.pathname.split("/").filter(Boolean);
        id = parts[parts.length - 1] || "";
      }
    } catch { /* keep raw */ }
    if (!id) return setJoinError("Couldn't read a meeting ID from that link.");
    navigate(`/v2/join/${id}`);
  };

  return (
    <div className="fm-page">
      <Ambient />
      <ThemeSlot />

      <div className="fm-page__inner">
        <Brand tagline="Sign in to start meetings — or jump straight into one." />

        <div className="fm-panels">
          {/* SIGN-IN PANEL */}
          <section className="fm-panel fm-panel--accent" aria-labelledby="lp-signin-title">
            <div className="fm-panel__head">
              <span className="fm-panel__eyebrow">🔐 For hosts</span>
              <h2 id="lp-signin-title" className="fm-panel__title">Sign in</h2>
              <p className="fm-panel__sub">Required to create new meetings.</p>
            </div>

            <form className="fm-form" onSubmit={handleSignIn} noValidate>
              <Input
                label="ID"
                required
                placeholder="Your FaceMeet ID"
                value={fdaId}
                onChange={(e) => setFdaId(e.target.value)}
                autoComplete="username"
                autoCapitalize="off"
                autoCorrect="off"
                spellCheck={false}
                autoFocus
              />

              <Input
                label="Password"
                required
                type={showPwd ? "text" : "password"}
                placeholder="Your password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="current-password"
                trailingButton={{
                  icon: showPwd ? "🙈" : "👁",
                  onClick: () => setShowPwd((v) => !v),
                  ariaLabel: showPwd ? "Hide password" : "Show password",
                }}
              />

              {signInError && (
                <div className="fm-inline-error" role="alert">
                  <span aria-hidden="true">⚠️</span>
                  <span>{signInError}</span>
                </div>
              )}

              <Button type="submit" variant="primary" size="lg" fullWidth loading={signingIn}>
                {signingIn ? "Signing in…" : "Sign in"}
              </Button>

              <p style={{ fontSize: "var(--text-xs)", color: "var(--text-faint)", textAlign: "center" }}>
                Signing in here will sign you out anywhere else.
              </p>
            </form>
          </section>

          {/* OR DIVIDER */}
          <div className="fm-panels__or" aria-hidden="true"><span>or</span></div>

          {/* QUICK JOIN PANEL */}
          <section className="fm-panel" aria-labelledby="lp-join-title">
            <div className="fm-panel__head">
              <span className="fm-panel__eyebrow">↗ For guests</span>
              <h2 id="lp-join-title" className="fm-panel__title">Join meeting</h2>
              <p className="fm-panel__sub">No sign-in needed — just a room link.</p>
            </div>

            <form className="fm-form" onSubmit={handleQuickJoin} noValidate>
              <Input
                label="Room ID or link"
                placeholder="abc-123-xyz or full invite link"
                value={roomId}
                onChange={(e) => setRoomId(e.target.value)}
                autoCapitalize="off"
                autoCorrect="off"
                spellCheck={false}
              />

              {joinError && (
                <div className="fm-inline-error" role="alert">
                  <span aria-hidden="true">⚠️</span>
                  <span>{joinError}</span>
                </div>
              )}

              <Button type="submit" variant="secondary" size="lg" fullWidth>
                Join meeting
              </Button>

              <p style={{ fontSize: "var(--text-xs)", color: "var(--text-faint)", textAlign: "center" }}>
                You'll enter your name on the next screen.
              </p>
            </form>
          </section>
        </div>
      </div>

      <IconSidebarV2 />
    </div>
  );
}
export default LoginPageV2;